import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import authFetch from '../utils/authFetch';

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';

const createMarkerIcon = (price) => {
  const formattedPrice = price
    ? price >= 1000000
      ? `${(price / 1000000).toFixed(1)}M`
      : price >= 1000
      ? `${(price / 1000).toFixed(0)}K`
      : String(price)
    : '—';

  return L.divIcon({
    html: `<div style="width:auto;height:auto;min-width:48px;padding:4px 8px;border-radius:999px;background:#dc2626;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;color:white;font-weight:600;font-size:12px;white-space:nowrap;">${formattedPrice}€</div>`,
    className: '',
    iconSize: [60, 28],
    iconAnchor: [30, 14]
  });
};

const PropertiesMapPage = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadProperties = async () => {
      try {
        setLoading(true);
        const response = await authFetch(`${API_BASE}/api/leads/selling?status=selling`);
        if (!response.ok) {
          throw new Error('Failed to load selling properties');
        }

        const data = await response.json();
        setProperties(Array.isArray(data) ? data.filter((entry) => {
          const lat = Number(entry.latitude);
          const lon = Number(entry.longitude);
          return Number.isFinite(lat) && Number.isFinite(lon) && (lat !== 0 || lon !== 0);
        }) : []);
      } catch (err) {
        console.error(err);
        setError('Could not load properties for the map.');
      } finally {
        setLoading(false);
      }
    };

    loadProperties();
  }, []);

  const propertyMarkers = useMemo(() => properties.map((property) => ({
    ...property,
    title: `${property.katastrskaObcina || ''} ${property.idParceleStavbe || ''}`.trim() || 'Selling property',
    latitude: Number(property.latitude),
    longitude: Number(property.longitude)
  })), [properties]);

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        <button
          type="button"
          onClick={() => navigate('/real-estate')}
          style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', backgroundColor: '#f8fafc', cursor: 'pointer' }}
        >
          <ArrowLeft size={16} />
        </button>
        <div>
          <h1 style={{ margin: 0, color: '#0f172a' }}>Properties Map</h1>
          <p style={{ margin: '4px 0 0', color: '#64748b' }}>All properties currently in selling status with coordinates.</p>
        </div>
      </div>

      {error ? <div style={{ color: '#dc2626', marginBottom: '16px' }}>{error}</div> : null}

      {loading ? (
        <div style={{ color: '#64748b' }}>Loading properties...</div>
      ) : propertyMarkers.length === 0 ? (
        <div style={{ color: '#64748b' }}>No properties with coordinates are available yet.</div>
      ) : (
        <div style={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 4px 10px rgba(15, 23, 42, 0.06)' }}>
          <MapContainer center={[propertyMarkers[0].latitude, propertyMarkers[0].longitude]} zoom={8} style={{ height: '640px', width: '100%' }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {propertyMarkers.map((property) => (
              <Marker key={property.id} position={[property.latitude, property.longitude]} icon={createMarkerIcon(property.price)}>
                <Popup>
                  <div style={{ minWidth: '180px' }}>
                    <div style={{ fontWeight: 700, marginBottom: '6px' }}>{property.title}</div>
                    <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '8px' }}>{property.coordinates || `${property.latitude}, ${property.longitude}`}</div>
                    <button
                      type="button"
                      onClick={() => navigate(`/selling/${property.id}`)}
                      style={{ border: 'none', borderRadius: '8px', backgroundColor: '#2563eb', color: 'white', padding: '6px 10px', cursor: 'pointer' }}
                    >
                      Open property
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      )}
    </div>
  );
};

export default PropertiesMapPage;
